import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Collapse, Select, Space, Typography, message } from 'antd';
import { PageLayout } from '../../ui';
import { useAuth } from '../../hooks/useAuth';
import { useGetRolesAccessQuery, useUpdateRolePermissionsMutation } from '../../store/api/usersApi';
import type { Role } from '../../types';

const RolesAccessPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const { data, isLoading, isError } = useGetRolesAccessQuery(undefined, { skip: !admin });
  const [updatePermissions, { isLoading: saving }] = useUpdateRolePermissionsMutation();

  const roles = useMemo(() => data?.roles ?? [], [data?.roles]);
  const catalog = data?.catalog ?? [];
  const whitelistKeys = useMemo(() => data?.whitelistKeys ?? [], [data?.whitelistKeys]);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId]
  );

  useEffect(() => {
    if (!roles.length || selectedRoleId !== null) return;
    const firstEditable = roles.find((r) => r.name !== 'admin') ?? roles[0];
    setSelectedRoleId(firstEditable?.id ?? null);
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selectedRole || !whitelistKeys.length) return;
    const raw = (selectedRole.permissions || {}) as Record<string, unknown>;
    const next: Record<string, boolean> = {};
    for (const k of whitelistKeys) {
      next[k] = Boolean(raw[k]);
    }
    setChecked(next);
  }, [selectedRole, whitelistKeys]);

  if (!admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const isAdminRole = selectedRole?.name === 'admin';

  const handleSave = async () => {
    if (!selectedRole || isAdminRole) return;
    try {
      await updatePermissions({ id: selectedRole.id, permissions: checked }).unwrap();
      message.success('Права сохранены. Изменения действуют при следующем запросе к API.');
    } catch (e: any) {
      message.error(e?.data?.error || e?.error || 'Не удалось сохранить');
    }
  };

  const collapseItems = catalog.map((group) => ({
    key: group.moduleKey,
    label: group.title,
    children: (
      <Space direction="vertical" style={{ width: '100%' }} size={[8, 8]}>
        {group.permissions.map((p) => (
          <Checkbox
            key={p.key}
            checked={!!checked[p.key]}
            disabled={isAdminRole || isLoading}
            onChange={(e) => setChecked((prev) => ({ ...prev, [p.key]: e.target.checked }))}
          >
            <Typography.Text>{p.label}</Typography.Text>
            <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              {p.key}
            </Typography.Text>
          </Checkbox>
        ))}
      </Space>
    )
  }));

  return (
    <PageLayout
      title="Роли и доступ"
      subtitle="Только ключи из whitelist; произвольные поля при сохранении отбрасываются на сервере."
      actions={
        <Space>
          <Link to="/administration/roles">Имена и описание ролей</Link>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!selectedRole || isAdminRole}>
            Сохранить права
          </Button>
        </Space>
      }
    >
      {isError && (
        <Alert type="error" showIcon message="Не удалось загрузить данные (нужна роль admin)." style={{ marginBottom: 16 }} />
      )}

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Typography.Text strong>Роль</Typography.Text>
          <Select<number>
            style={{ width: '100%', maxWidth: 420, marginTop: 8, display: 'block' }}
            loading={isLoading}
            value={selectedRoleId ?? undefined}
            options={roles.map((r: Role) => ({
              label: r.name === 'admin' ? `${r.name} (только просмотр)` : r.name,
              value: r.id
            }))}
            onChange={(id) => setSelectedRoleId(id)}
          />
        </div>

        {isAdminRole && (
          <Alert
            type="info"
            showIcon
            message="Роль admin"
            description={
              <>
                Права не редактируются через этот интерфейс. Значение в БД:
                <pre style={{ marginTop: 8, fontSize: 12, overflow: 'auto' }}>
                  {JSON.stringify(selectedRole?.permissions ?? {}, null, 2)}
                </pre>
              </>
            }
          />
        )}

        {!isAdminRole && (
          <Collapse items={collapseItems} defaultActiveKey={catalog.map((g) => g.moduleKey)} />
        )}
      </Space>
    </PageLayout>
  );
};

export default RolesAccessPage;
